import { useState } from "react";
import { Check, Crown, Loader2, LogOut, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCreateCheckout, useCustomerPortal } from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import logoFreelaDash from "@/assets/logo-freeladash.png";

const PRO_FEATURES = [
  "Projetos e clientes ilimitados",
  "Timer avançado com categorias",
  "Dashboard com inteligência financeira",
  "Relatórios completos e exportação",
  "Calculadora de preços profissional",
  "Alertas inteligentes e projeções",
  "Suporte prioritário",
];

export function TrialExpiredPaywall() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { toast } = useToast();
  const createCheckout = useCreateCheckout();
  const customerPortal = useCustomerPortal();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleSubscribe = async (plan: 'mensal' | 'anual') => {
    setLoadingPlan(plan);
    try {
      const { url } = await createCheckout.mutateAsync(plan);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível iniciar o checkout. Tente novamente.",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const { url } = await customerPortal.mutateAsync();
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Portal error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível abrir o portal de assinatura.",
      });
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-3xl space-y-8 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <img
            src={logoFreelaDash}
            alt="FreelaDash"
            className="h-20 w-20 rounded-2xl object-contain mx-auto shadow-lg"
          />
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">
              Para continuar usando o FreelaDash, assine o Pro!
            </h1>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">
              Seu período de teste terminou. Escolha um plano para desbloquear todas as funcionalidades.
            </p>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Monthly Plan */}
          <Card className="relative overflow-hidden border-2 border-primary shadow-lg">
            <div className="absolute top-3 right-3">
              <Badge className="bg-primary gap-1">
                <Sparkles className="h-3 w-3" />
                Recomendado
              </Badge>
            </div>
            <CardContent className="p-6 space-y-5">
              <div>
                <h3 className="font-semibold text-lg">Pro Mensal</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Todos os recursos para freelancers
                </p>
              </div>
              <div>
                <span className="text-4xl font-bold">R$ 49,90</span>
                <span className="text-muted-foreground">/mês</span>
              </div>
              <ul className="space-y-2.5">
                {PRO_FEATURES.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full gap-2"
                size="lg"
                onClick={() => handleSubscribe('mensal')}
                disabled={loadingPlan === 'mensal'}
              >
                {loadingPlan === 'mensal' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Crown className="h-4 w-4" />
                    Assinar Pro Mensal
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Annual Plan */}
          <Card className="border shadow-sm">
            <CardContent className="p-6 space-y-5">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-lg">Pro Anual</h3>
                  <Badge variant="secondary">33% OFF</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Economia de 2 meses
                </p>
              </div>
              <div>
                <span className="text-4xl font-bold">R$ 399,90</span>
                <span className="text-muted-foreground">/ano</span>
                <p className="text-sm text-muted-foreground mt-1">
                  Equivale a R$ 33,33/mês
                </p>
              </div>
              <ul className="space-y-2.5">
                {PRO_FEATURES.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                className="w-full gap-2"
                size="lg"
                variant="outline"
                onClick={() => handleSubscribe('anual')}
                disabled={loadingPlan === 'anual'}
              >
                {loadingPlan === 'anual' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processando...
                  </>
                ) : (
                  'Assinar Pro Anual'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer actions */}
        <div className="flex flex-col items-center gap-3 text-center">
          <button
            onClick={handleManageSubscription}
            className="text-sm text-primary hover:underline transition-colors"
            disabled={customerPortal.isPending}
          >
            {customerPortal.isPending ? "Abrindo portal..." : "Já assinei - Gerenciar assinatura existente"}
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sair da conta
          </button>
        </div>
      </div>
    </div>
  );
}
